/**
 * Safely coerces a raw value into a specific enum value.
 * If the value does not exist in the enum, returns null.
 *
 * @typeParam T The enum-like type to coerce into.
 * @param EnumType The enum-like object containing valid values.
 * @param value The raw value to convert.
 * @returns The matching enum value if found; otherwise, null.
 */
export function tryToEnum<T extends Record<string, string | number>>(
    EnumType: T,
    value: unknown,
): T[keyof T] | null {
    if (value == null) {
        return null;
    }

    const values = Object.values(EnumType) as unknown[];
    return (values.includes(value) ? value : null) as T[keyof T] | null;
}

/**
 * Safely coerces an array of raw values into an array of enum values.
 * Any values that do not exist in the enum are filtered out.
 * Returns null if the input is not an array or no valid enum values are found.
 *
 * @typeParam T The enum-like type to coerce into.
 * @param EnumType The enum-like object containing valid values.
 * @param rawArray The raw array to convert.
 * @returns An array of matching enum values if any are found; otherwise, null.
 */
export function tryToEnumArray<T extends Record<string, string | number>>(
    EnumType: T,
    rawArray: unknown,
): T[keyof T][] | null {
    if (!Array.isArray(rawArray)) {
        return null;
    }

    const values = Object.values(EnumType) as unknown[];
    const filtered = rawArray.filter((v) => values.includes(v)) as T[keyof T][];

    return filtered.length ? filtered : null;
}

/**
 * Safely extracts enum values from a TypeScript enum.
 * Handles both numeric and string enums correctly.
 *
 * @param enumeration - The enum object to extract values from
 * @returns Array of enum values
 *
 * @example
 * enum NumericEnum { Active = 1, Inactive = 2 }
 * getEnumValues(NumericEnum) // [1, 2]
 *
 * @example
 * enum StringEnum { Active = 'active', Inactive = 'inactive' }
 * getEnumValues(StringEnum) // ['active', 'inactive']
 */
export function getEnumValues<T extends Record<string, string | number>>(
    enumeration: T,
): Array<T[keyof T]> {
    return Object.keys(enumeration)
        .filter((key) => isNaN(Number(key)))
        .map((key) => enumeration[key]) as Array<T[keyof T]>;
}
